import { cn } from "@/utils/helpers"
import { cva } from "class-variance-authority"
import {
  CSSProperties,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { Icon } from "../icon/Icon"
import { InputFieldAdornment } from "./InputFieldAdornment"

const InputWrapperVariants = cva(
  "flex w-full h-14 rounded-[0.25rem] transition-opacity has-[:disabled]:cursor-not-allowed has-[:disabled]:bg-surface-disabled items-center relative",
  {
    variants: {
      variant: {
        default: "",
        medium: "font-medium",
        semibold: "font-semibold",
        bold: "font-bold",
      },
      type: {
        default: "",
        hyperlink: "cursor-pointer underline",
        upperCase: "uppercase",
      },
    },
    defaultVariants: {
      variant: "default",
      type: "default",
    },
  },
)
const InputFieldVariants = cva(
  "peer h-full w-full min-w-0 bg-transparent placeholder-on-surface-variant outline-none rounded-[0.25rem] disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        default: "",
        medium: "font-medium",
        semibold: "font-semibold",
        bold: "font-bold",
      },
      type: {
        default: "",
        hyperlink: "cursor-pointer underline",
        upperCase: "uppercase",
      },
    },
    defaultVariants: {
      variant: "default",
      type: "default",
    },
  },
)
const InputBorderVariants = cva(
  "rounded-[0.25rem] absolute z-[9] size-full top-0 left-0 right-0 bottom-0 pointer-events-none flex border border-on-surface-variant peer-focus:border-primary peer-focus:border-3",
  {
    variants: {
      variant: {
        default: "",
        medium: "font-medium",
        semibold: "font-semibold",
        bold: "font-bold",
      },
      type: {
        default: "",
        hyperlink: "cursor-pointer underline",
        upperCase: "uppercase",
      },
    },
    defaultVariants: {
      variant: "default",
      type: "default",
    },
  },
)

export interface InputFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "prefix"> {
  maxLength?: number
  startAdornment?: React.ReactNode
  startAdornmentClassName?: string
  endAdornment?: React.ReactNode
  hasError?: boolean
  isCompleted?: boolean
  prefix?: React.ReactNode
  suffix?: React.ReactNode
  inputContainerClassName?: string
  inputBorderClassName?: string
  hasErrorIcon?: boolean
  action?: React.ReactNode
  actionDisabled?: boolean
  approvalStatusIcon?: React.ReactNode
}

export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      className,
      inputContainerClassName,
      inputBorderClassName,
      style,
      startAdornment,
      startAdornmentClassName,
      endAdornment,
      isCompleted,
      hasError,
      hasErrorIcon,
      onChange,
      prefix,
      suffix,
      action,
      actionDisabled,
      approvalStatusIcon,
      ...props
    },
    ref,
  ) => {
    const [localValue, setLocalValue] = useState<string>(
      props.defaultValue?.toString?.() ?? "",
    )
    const isControlled = useRef(false)
    useLayoutEffect(() => {
      setLocalValue(props.value?.toString?.() ?? "")
      if (props.value !== undefined) {
        isControlled.current = true
      }
    }, [props.value])
    const inputRef = useRef<HTMLInputElement>(null)
    useImperativeHandle(ref, () => inputRef.current)
    useEffect(() => {
      if (!isControlled.current && inputRef.current?.value) {
        setLocalValue(inputRef.current.value)
      }
    }, [])
    const spanRef = useRef<HTMLSpanElement>(null)

    const [suffixWidth, setSuffixWidth] = useState(0)
    const [prefixWidth, setPrefixWidth] = useState(0)
    const [adornmentRightWidth, setAdornmentRightWidth] = useState(0)
    const [adornmentLeftWidth, setAdornmentLeftWidth] = useState(0)

    const [inputValueWidth, setInputValueWidth] = useState(0)
    useLayoutEffect(() => {
      const inputValueWidth =
        spanRef.current?.getBoundingClientRect()?.width ?? 0

      setInputValueWidth(inputValueWidth)
    }, [localValue, props.placeholder])

    const [inputFieldWidth, setInputFieldWidth] = useState(0)
    useLayoutEffect(() => {
      const inputFieldWidth =
        inputRef.current?.getBoundingClientRect().width ?? 0
      setInputFieldWidth(inputFieldWidth)
    }, [adornmentRightWidth, adornmentLeftWidth, suffixWidth, prefixWidth])

    useEffect(() => {
      if (!prefix) {
        setPrefixWidth(0)
      }
    }, [prefix])
    useEffect(() => {
      if (!suffix) {
        setSuffixWidth(0)
      }
    }, [suffix])

    const [direction, setDirection] = useState("")
    const [fontStyle, setFontStyle] = useState<CSSProperties>({})
    useLayoutEffect(() => {
      const observeTarget = inputRef.current
      if (!observeTarget) {
        return
      }
      const computedStyle = getComputedStyle(observeTarget as Element)
      setFontStyle({
        fontSize: computedStyle.fontSize,
        fontWeight: computedStyle.fontWeight,
        fontFamily: computedStyle.fontFamily,
        fontStyle: computedStyle.fontStyle,
        letterSpacing: computedStyle.letterSpacing,
      })
      const mutationObserver = new MutationObserver((entries) => {
        for (const entry of entries) {
          if (entry.type === "attributes") {
            if (entry.attributeName === "dir") {
              const computedStyle = getComputedStyle(entry.target as Element)
              setDirection(computedStyle?.direction)
            } else if (
              entry.attributeName === "style" ||
              entry.attributeName === "class"
            ) {
              const computedStyle = getComputedStyle(entry.target as Element)
              setFontStyle({
                fontSize: computedStyle.fontSize,
                fontWeight: computedStyle.fontWeight,
                fontFamily: computedStyle.fontFamily,
                fontStyle: computedStyle.fontStyle,
                letterSpacing: computedStyle.letterSpacing,
              })
            }
          }
        }
      })

      mutationObserver.observe(observeTarget, {
        attributes: true,
        attributeFilter: ["dir", "style", "class"],
      })

      return () => {
        mutationObserver.disconnect()
      }
    }, [])

    const inputPaddingRight = useMemo(() => {
      return suffixWidth + adornmentRightWidth
    }, [adornmentRightWidth, suffixWidth])
    const inputPaddingLeft = useMemo(() => {
      return prefixWidth + adornmentLeftWidth
    }, [adornmentLeftWidth, prefixWidth])
    const { suffixOffset, prefixOffset } = useMemo(() => {
      // console.log(
      //   "inputPaddingLeft",
      //   inputPaddingLeft,
      //   "inputPaddingRight",
      //   inputPaddingRight,
      //   "inputFieldWidth",
      //   inputFieldWidth,
      //   "inputValueWidth",
      //   inputValueWidth,
      // )
      const leftOffset = inputPaddingLeft + inputValueWidth
      const leftMaxOffset = inputFieldWidth - inputPaddingLeft
      const rightOffset = inputPaddingRight + inputValueWidth
      const rightMaxOffset = inputFieldWidth - inputPaddingRight
      return {
        suffixOffset:
          direction !== "rtl"
            ? Math.min(leftOffset, rightMaxOffset)
            : rightMaxOffset,
        prefixOffset:
          direction !== "rtl"
            ? leftMaxOffset
            : Math.min(rightOffset, leftMaxOffset),
      }
    }, [
      inputFieldWidth,
      inputPaddingRight,
      inputPaddingLeft,
      inputValueWidth,
      direction,
    ])

    const validationIcon = useMemo<React.ReactNode>(() => {
      if (isCompleted) {
        return <Icon icon="check_circle" className="text-success" />
      }
      if (hasErrorIcon && hasError) {
        return <Icon icon="error" fill className="text-error" />
      }
    }, [hasError, hasErrorIcon, isCompleted])

    const hasTrailingAction = Boolean(approvalStatusIcon || action)
    const actionSegmentClassName = cn(
      "border-outline flex h-14 w-14 shrink-0 items-center justify-center border border-l-0",
      hasError && "border-error border-2 border-l-0",
      actionDisabled && "pointer-events-none opacity-50",
      props.disabled && "bg-surface-disabled",
    )

    return (
      <div className="flex w-full">
        <div
          className={cn(
            InputWrapperVariants({
              variant: "default",
            }),
            hasTrailingAction ? "min-w-0 flex-1" : "w-full",
            inputContainerClassName,
          )}
        >
          <input
            key={props.id}
            id={props.id}
            className={cn(
              InputFieldVariants({ variant: "default" }),
              hasTrailingAction && "rounded-r-none",
              className,
            )}
            ref={inputRef}
            style={{
              paddingLeft: inputPaddingLeft,
              paddingRight: inputPaddingRight,
              ...style,
            }}
            onChange={(e) => {
              onChange?.(e)
              if (!isControlled.current) {
                setLocalValue(e.target.value)
              }
            }}
            aria-invalid={hasError}
            {...props}
          />
          <InputFieldAdornment
            className={cn(
              "pointer-events-none absolute left-0 flex h-fit min-w-4",
              startAdornment && "pl-3 pr-2",
              startAdornmentClassName,
            )}
            onWidthChange={setAdornmentLeftWidth}
          >
            {startAdornment}
          </InputFieldAdornment>
          <InputFieldAdornment
            className={cn(
              "pointer-events-none absolute right-0 flex h-fit min-w-4 gap-3",
              (validationIcon || endAdornment) && "pl-2 pr-3",
            )}
            onWidthChange={setAdornmentRightWidth}
          >
            {validationIcon}
            {endAdornment}
          </InputFieldAdornment>
          <div
            className={cn(
              InputBorderVariants({ variant: "default" }),
              hasError && "border-error border-2",
              hasTrailingAction && "rounded-r-none",
              inputBorderClassName,
            )}
          />
          <span
            className="invisible absolute w-max max-w-full overflow-hidden whitespace-pre"
            style={fontStyle}
            ref={spanRef}
          >
            {localValue !== "" ? localValue : props.placeholder}
          </span>
          {suffix && (
            <InputFieldAdornment
              className="text-on-surface-variant text-body-base pointer-events-none absolute bottom-0 top-0 select-none content-center whitespace-pre"
              style={{
                left: suffixOffset,
              }}
              onWidthChange={setSuffixWidth}
              aria-hidden
            >
              {suffix}
            </InputFieldAdornment>
          )}
          {prefix && (
            <InputFieldAdornment
              className="text-on-surface-variant text-body-base pointer-events-none absolute bottom-0 top-0 select-none content-center whitespace-pre"
              style={{
                right: prefixOffset,
              }}
              onWidthChange={setPrefixWidth}
              aria-hidden
            >
              {prefix}
            </InputFieldAdornment>
          )}
        </div>
        {approvalStatusIcon && (
          <div className={cn(actionSegmentClassName, !action && "rounded-r")}>
            {approvalStatusIcon}
          </div>
        )}
        {action && (
          <div className={cn(actionSegmentClassName, "rounded-r")}>
            {action}
          </div>
        )}
      </div>
    )
  },
)

InputField.displayName = "InputField"
